function ParkChicago() {
    return (
        <div className="project-page">
            {/* Project Header */}
            <h1 className="github-link">
                ParkChicago Design Audit
            </h1>

            {/* Overview Section */}
            <div className="tech-tools">
                <div className="project-duration">
                    <p><strong>Duration:</strong> 5 weeks</p>
                    <p><strong>Role:</strong> Researcher, UX Strategist</p>
                    <p><strong>Team Members:</strong> 5</p>
                    <p>
                        <strong>My contribution:</strong> Conducted a research-led design audit that translated
                        heuristic violations and user pain points into a strategic roadmap.
                    </p>
                </div>
            </div>

            <div className="project-image">
                <img src="/academic/parkchicago.png" alt="Original Park Chicago Interface"></img>
            </div>

            <hr />

            <h2>Phase 1: Heuristic Audit</h2>
            <p>We evaluated the existing interface against Nielsen’s 10 Usability Heuristics to pinpoint why users felt overwhelmed.</p>
            <ul>
                <li><strong>Recognition vs. Recall:</strong> Saved zones relied on 6-digit codes.</li>
                        <span className="img-descript">↑ Users had to physically walk back to signs to verify numbers.</span>
                <li><strong>Aesthetic & Minimalist Design:</strong> "Banner Blindness" from massive text blocks.</li>
                        <span className="img-descript">↑ Critical instructions were ignored.</span>
                <li><strong>Fractured System Match:</strong> Map features lived in a separate app.</li>
                        <span className="img-descript">↑  Broken user flow during the "Find" phase.</span>
                <li><strong>Visual Hierarchy:</strong> Background imagery interfered with text contrast. </li>
                        <span className="img-descript">↑ Poor outdoor legibility in sunlight/glare.</span>
            </ul>

            <div className="project-image">
                <img src="/academic/pc2.png" alt="Annotated Heuristic Audit"></img>
            </div>

            <h2>Phase 2: User Research</h2>
            <p>We interviewed 11 frequent Chicago drivers to validate the audit findings. The sentiment was unanimous: the app felt "greedy" and "confusing."</p>
           <ul>
                <li><strong>Mandatory minimum balance:</strong> 7/11 participants had an issue with the mandatory $20 minimum balance.</li>
                <li><strong>Visual design: </strong>Participants said the current app’s visuals were “hard to look at” and “outdated.”</li>
                <li><strong>Map integration: </strong>Users either did not know about the map or did not want to download a separate app.</li>
                <li><strong>Zone representation:</strong> Users were unsure which physical parking spot the zone number represented.</li>
            </ul>

            <h2>Phase 3: Strategic Roadmap</h2>
            <p>We translated findings into 8 core requirements, prioritizing <strong>Ethical Design</strong> and <strong>Efficiency.</strong></p>
            <div className="requirements-grid">
                <ul>
                    <li><strong>Frictionless Payments:</strong> Apple/Google Pay integration (No $20 minimum).</li>
                    <li><strong>Ecosystem Consolidation:</strong> Single-app with a map & parking: find-to-pay flow.</li>
                    <li><strong>Recognition-Based Navigation:</strong> Nicknames and icons for saved zones.</li>
                    <li><strong>Active Consent:</strong> Transformed passive warnings into actionable validation steps.</li>
                    <li><strong>Persistent Wayfinding:</strong> Standardized bottom navigation bar.</li>
                    <li><strong>Minimize Cognitive Load:</strong> High contrast elements, limited text, and intuitive iconography.</li>
                </ul>
            </div>

            <h2>The Result: A Validated Redesign</h2>
            <p>Through iterative prototyping and usability testing, we refined the interface to ensure the final design was both validated by user data and optimized for high-stress environments.
            </p>
            <div className="project-image">
                <img src="/previews/parkchicago.png" alt="Redesign Screen 1"></img>
                <img src="/previews/parkchicago5.png" alt="Redesign Screen 2"></img>
            </div>

            <h2>Key Improvements</h2>
            <div className="improvements-list">
                <ul>
                    <li><strong>Customizable Recents:</strong> Added names, icons, and "Favorite" pinning.</li>
                    <li><strong>Validation Events:</strong> Replaced the "Wall of Text" with a confirmation modal that appears only when needed.</li>
                    <li><strong>High-Contrast UI:</strong> Removed visual noise for 100% outdoor readability.</li>
                    <li><strong>Integrated Map:</strong> Embedded GPS-linked zone detection directly into the home screen.</li>
                </ul>
            </div>

            <p className="disclaimer">
                <strong>Note:</strong> While I led the research, audit, and strategic requirements, the final 
                visual identity and UI iterations were executed by my design teammates. This is a course project.
            </p>
        </div>
    );
};

export default ParkChicago;